import * as types from './actionTypes';
import LayerActions from '../layer/actions';
import AgentRegistryActions from '../agentRegistry/actions';

import MapClient from '../../focus-client/map-client';
import AuthClient from '../../focus-client/auth-client';

import { createQuery } from '../../utils/queryUtil';
import {
  parseHeatmapResponse,
  heatmapGridParam,
  metadataBoundsToObject
} from '../../utils/heatmapUtil';
import { fetchAllFeaturePages } from '../../utils/paginationUtil';

// Per-request page size, not a total cap - getLayerFeatures below now pages
// past this via fetchAllFeaturePages whenever a query has more results than
// one page, so a viewport with more than 5000 tracks is fetched in full
// rather than silently truncated at the first 5000 (see paginationUtil.js).
const TRACK_MARKER_PAGE_LENGTH = 5000;

const completeLogin = user => ({
  type: types.NETWORK_LOGIN_SUCCESS,
  payload: { user }
});

const unhappyLogin = message => ({
  type: types.NETWORK_LOGIN_FAILURE,
  payload: { message }
});

const setCurrentUser = username => ({
  type: types.SET_CURRENT_USER,
  payload: { username }
});

const submitLogin = (username, password, extraArgs = {}) => {
  var config = {
    baseURI: document.baseURI
  };

  return dispatch => {
    let auth = AuthClient(config);

    return auth.login(username, password).then(json => {
      if (json.authenticated) {
        dispatch(setCurrentUser(username));
        dispatch(completeLogin({ username }));
      } else if (json.isError) {
        dispatch(unhappyLogin(json.error.message));
      }
    });
  };
};

const completeNetworkLogout = username => ({
  type: types.NETWORK_LOGOUT_SUCCESS,
  payload: { username }
});

const submitLogout = (username, extraArgs = {}) => {
  var config = {
    baseURI: document.baseURI
  };

  return dispatch => {
    let auth = AuthClient(config);

    return auth.logout(username).then(response => {
      if (response.ok) {
        dispatch(completeNetworkLogout(username));
      }
    });
  };
};

const localLogout = () => ({
  type: types.LOCAL_LOGOUT
});

const getAuthenticationStatus = (extraArgs = {}) => {
  var config = {
    baseURI: document.baseURI
  };

  return dispatch => {
    let auth = AuthClient(config);

    return auth.status().then(response => {
      if (response.authenticated) {
        dispatch({
          type: types.FETCH_AUTHSTATUS_SUCCESS,
          payload: { user: response }
        });
      }
    });
  };
};

const describeMap = () => {
  var config = {
    baseURI: document.baseURI,
    searchType: 'all'
  };

  return dispatch => {
    let map = MapClient(config);

    return map.describe('describe-map').then(response => {
      if (!response.isError) {
        dispatch(LayerActions.setProjectLayers(response.models));
      } else if (response.error.status === 401) {
        dispatch(localLogout());
      }
    });
  };
};

const getAlertLayerFeature = () => {
  var config = {
    baseURI: document.baseURI,
    searchType: 'all'
  };

  return (dispatch, getState) => {
    let mapLayer = getState().layer.layers.find(
      item => item.name === 'Observations'
    );
    let alertQuery = createQuery(
      false,
      false,
      false,
      false,
      false,
      null,
      null,
      getState().query.areas,
      [],
      getState().query.mode,
      [],
      []
    );

    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);

    alertQuery.withDateTimeRange(now, oneHourAgo);

    let map = MapClient(config);

    return map
      .features('alerts', mapLayer.projectId, mapLayer.id, alertQuery, 1, 500)
      .then(response => {
        if (!response.isError) {
          response.features = response.features.filter(item => {
            if (item.properties.EntityClass === 'alert') {
              return item;
            }
          });
          let selectedItem = null;
          if (getState().timeline.selectedItem.observationId) {
            selectedItem = getState().timeline.selectedItem.observationId;
          } else if (getState().timeline.selectedItem.featureId) {
            selectedItem = getState().timeline.selectedItem.featureId;
          }

          let selectedTrackObservations = null;
          if (getState().timeline.selectedTrackObservations.length > 0) {
            selectedTrackObservations = getState().timeline.selectedTrackObservations.map(
              item => item.Observation.observationId
            );
          }
          dispatch(
            LayerActions.setAlertFeatures(
              mapLayer.projectId,
              mapLayer.id,
              response,
              selectedItem,
              selectedTrackObservations
            )
          );
        } else if (response.error.status === 401) {
          dispatch(localLogout());
        }
      });
  };
};

const getAgentLayerFeatures = () => {
  return (dispatch, getState) => {
    let mapLayer = getState().layer.layers.find(item => item.name === 'Agents');
    if (!mapLayer) return;

    let coordinates = {
      north: getState().query.north,
      east: getState().query.east,
      south: getState().query.south,
      west: getState().query.west
    };

    let query = createQuery(
      false,
      false,
      false,
      false,
      false,
      null,
      coordinates,
      getState().query.areas,
      getState().query.concepts,
      getState().query.mode,
      getState().query.dataSources,
      getState().query.agents
    );

    delete query.options;

    if (getState().layer.selectedLayers.includes(mapLayer.name)) {
      dispatch(
        getLayerFeatures(
          mapLayer.name,
          mapLayer.projectId,
          mapLayer.id,
          query,
          1,
          500
        )
      );
    }
  };
};

const getAllLayerFeatures = (options = {}) => {
  const {
    onlyCommon = false,
    onLayerProgress = null,
    commonLayerNames = null
  } = options;
  const isCommonLayer = layerName =>
    layerName === 'Observations' ||
    layerName === 'Tracks' ||
    layerName === 'Agents';
  const commonLayerOrder = {
    Observations: 1,
    Agents: 2,
    Tracks: 3
  };

  return (dispatch, getState) => {
    let coordinates = {
      north: getState().query.north,
      east: getState().query.east,
      south: getState().query.south,
      west: getState().query.west
    };
    const candidateLayers = getState()
      .layer.layers.filter(item => item.projectId !== '123456')
      .filter(item => (onlyCommon ? isCommonLayer(item.name) : true));
    const scopedCandidateLayers =
      onlyCommon && Array.isArray(commonLayerNames) && commonLayerNames.length
        ? candidateLayers.filter(item => commonLayerNames.includes(item.name))
        : candidateLayers;
    const mapLayers = onlyCommon
      ? scopedCandidateLayers.sort(
          (a, b) =>
            (commonLayerOrder[a.name] || 99) - (commonLayerOrder[b.name] || 99)
        )
      : scopedCandidateLayers;

    const fetchMapLayer = mapLayer => {
      let query = null;
      if (mapLayer.name !== 'Agents') {
        if (mapLayer.projectId == 'default') {
          query = createQuery(
            false,
            true,
            true,
            false,
            true,
            null,
            coordinates,
            getState().query.areas,
            getState().query.concepts,
            getState().query.mode,
            getState().query.dataSources,
            getState().query.agents
          );
        } else {
          query = createQuery(
            false,
            false,
            false,
            false,
            false,
            null,
            coordinates,
            getState().query.areas,
            getState().query.concepts,
            getState().query.mode,
            getState().query.dataSources,
            getState().query.agents
          );
        }
      } else if (mapLayer.name === 'Agents') {
        if (mapLayer.projectId == 'default') {
          query = createQuery(
            false,
            false,
            false,
            false,
            false,
            null,
            coordinates,
            getState().query.areas,
            getState().query.concepts,
            getState().query.mode,
            getState().query.dataSources,
            getState().query.agents
          );
        } else {
          query = createQuery(
            false,
            false,
            false,
            false,
            false,
            null,
            coordinates,
            getState().query.areas,
            getState().query.concepts,
            getState().query.mode,
            getState().query.dataSources,
            getState().query.agents
          );
        }
      }

      if (mapLayer.projectId == 'default') {
        if (getState().query.searchQuery !== '' && mapLayer.name !== 'Agents')
          query.withQueryText(getState().query.searchQuery);
        if (
          getState().query.start !== null &&
          getState().query.end !== null &&
          mapLayer.name !== 'Agents'
        )
          query.withDateTimeRange(getState().query.end, getState().query.start);
      }
      delete query.options;
      const pageLength =
        mapLayer.name === 'Tracks'
          ? TRACK_MARKER_PAGE_LENGTH
          : mapLayer.name === 'Observations' &&
            getState().ontology.showObservationClusters
          ? getState().ontology.observationClusterThreshold || 500
          : 500;
      const requestParams =
        mapLayer.name === 'Observations' &&
        getState().ontology.showObservationClusters
          ? { heatmap: '5x5' }
          : null;
      if (getState().layer.selectedLayers.includes(mapLayer.name)) {
        return dispatch(
          getLayerFeatures(
            mapLayer.name,
            mapLayer.projectId,
            mapLayer.id,
            query,
            1,
            pageLength,
            requestParams
          )
        );
      }
      return Promise.resolve(null);
    };

    const pendingLayers = onlyCommon
      ? mapLayers.map(mapLayer => mapLayer.name)
      : [];
    const notifyProgress = () => {
      if (!onlyCommon || typeof onLayerProgress !== 'function') return;
      onLayerProgress(pendingLayers.slice());
    };
    notifyProgress();

    const requests = mapLayers.map(mapLayer =>
      Promise.resolve(fetchMapLayer(mapLayer)).finally(() => {
        const index = pendingLayers.indexOf(mapLayer.name);
        if (index >= 0) {
          pendingLayers.splice(index, 1);
        }
        notifyProgress();
      })
    );
    return Promise.allSettled(requests);
  };
};

// Fetches the MarkLogic box-grid heatmap for a single layer (Observations or
// Tracks) over the current map bounds + active filters, using the fast
// returnFeatures=false path (see sensingcluesr::get_heatmap v1.1.0). The
// parsed grid GeoJSON is stored per layer in the layer slice.
const getLayerHeatmap = layerName => {
  var config = {
    baseURI: document.baseURI,
    searchType: 'all'
  };

  return (dispatch, getState) => {
    const mapLayer = getState().layer.layers.find(
      item => item.projectId == 'default' && item.name === layerName
    );
    if (!mapLayer) return Promise.resolve(null);

    // The viewport bounds only decide *what matches* (the geo filter on the
    // query below). They are not what the backend grids over: confirmed by
    // requesting the same query at different heatmap=ColsxRows resolutions
    // and getting back an identical metadata.bounds both times - the raster
    // extent is server-computed and independent of both the viewport and
    // the requested resolution. So the ColsxRows we ask for has to be
    // matched to that server-reported extent, not to the viewport, or the
    // returned cells come out non-square.
    const bounds = {
      north: getState().query.north,
      east: getState().query.east,
      south: getState().query.south,
      west: getState().query.west
    };

    const query = createQuery(
      false,
      true,
      true,
      false,
      true,
      null,
      bounds,
      getState().query.areas,
      getState().query.concepts,
      getState().query.mode,
      getState().query.dataSources,
      getState().query.agents
    );
    if (getState().query.searchQuery !== '')
      query.withQueryText(getState().query.searchQuery);
    if (getState().query.start !== null && getState().query.end !== null)
      query.withDateTimeRange(getState().query.end, getState().query.start);
    delete query.options;

    const resolution =
      layerName === 'Tracks'
        ? getState().ontology.trackHeatmapResolution || 25
        : getState().ontology.observationHeatmapResolution || 25;

    const map = MapClient(config);

    const handleResponse = response => {
      if (response && response.isError) {
        if (response.error && response.error.status === 401)
          dispatch(localLogout());
        return null;
      }
      const { geojson } = parseHeatmapResponse(response);
      dispatch(LayerActions.setLayerHeatmap(layerName, geojson));
      return geojson;
    };

    // Step 1: a cheap probe (its own resolution is irrelevant - we only
    // read back metadata.bounds, never its boxes) to learn the extent the
    // backend will actually grid over for this query. 1x1 confirmed
    // supported by the backend - no need to ask for more than one box.
    const probeParams = {
      returnQuery: true,
      returnFeatures: false,
      heatmap: '1x1'
    };

    return map
      .features(
        layerName + '-heatmap-probe',
        mapLayer.projectId,
        mapLayer.id,
        query,
        0,
        1,
        probeParams
      )
      .then(probeResponse => {
        if (probeResponse && probeResponse.isError) {
          if (probeResponse.error && probeResponse.error.status === 401) {
            dispatch(localLogout());
            return null;
          }
          // Probe failed - fall back to the viewport bounds rather than
          // not requesting a heatmap at all.
        }
        const gridBounds =
          (probeResponse &&
            probeResponse.metadata &&
            metadataBoundsToObject(probeResponse.metadata.bounds)) ||
          bounds;

        // Step 2: the real request, using the resolution the backend will
        // actually grid over so the returned cells come back square.
        const requestParams = {
          returnQuery: true,
          returnFeatures: false,
          heatmap: heatmapGridParam(gridBounds, resolution)
        };

        return map
          .features(
            layerName + '-heatmap',
            mapLayer.projectId,
            mapLayer.id,
            query,
            0,
            1,
            requestParams
          )
          .then(handleResponse);
      });
  };
};

const getSpecificLayer = layer => {
  return (dispatch, getState) => {
    let coordinates = {
      north: getState().query.north,
      east: getState().query.east,
      south: getState().query.south,
      west: getState().query.west
    };
    let query = null;

    if (layer.projectId == 'default') {
      query = createQuery(
        false,
        true,
        true,
        false,
        true,
        null,
        coordinates,
        getState().query.areas,
        getState().query.concepts,
        getState().query.mode,
        getState().query.dataSources,
        getState().query.agents
      );

      if (getState().query.searchQuery !== '')
        query.withQueryText(getState().query.searchQuery);
      if (
        getState().query.start !== null &&
        getState().query.end !== null &&
        layer.name !== 'Agents'
      )
        query.withDateTimeRange(getState().query.end, getState().query.start);

      delete query.options;
    } else if (layer.projectId === 'track') {
      query = createQuery(
        false,
        false,
        false,
        false,
        false,
        null,
        coordinates,
        getState().query.areas,
        getState().query.concepts,
        getState().query.mode,
        getState().query.dataSources,
        getState().query.agents
      );

      delete query.options;
    }

    const pageLength =
      layer.name === 'Tracks'
        ? TRACK_MARKER_PAGE_LENGTH
        : layer.name === 'Observations' &&
          getState().ontology.showObservationClusters
        ? getState().ontology.observationClusterThreshold || 500
        : 500;
    const requestParams =
      layer.name === 'Observations' &&
      getState().ontology.showObservationClusters
        ? { heatmap: '5x5' }
        : null;

    dispatch(
      getLayerFeatures(
        layer.name,
        layer.projectId,
        layer.id,
        query,
        1,
        pageLength,
        requestParams
      )
    );
  };
};

const getLayerFeatures = (
  signalKey,
  projectId,
  layerId,
  searchQuery,
  start,
  pageLength,
  requestParams = null
) => {
  var config = {
    baseURI: document.baseURI,
    searchType: 'all'
  };

  return (dispatch, getState) => {
    let map = MapClient(config);

    const getSelectionState = () => {
      let selectedItem = null;
      if (getState().timeline.selectedItem.observationId) {
        selectedItem = getState().timeline.selectedItem.observationId;
      } else if (getState().timeline.selectedItem.featureId) {
        selectedItem = getState().timeline.selectedItem.featureId;
      }

      let selectedTrackObservations = null;
      if (getState().timeline.selectedTrackObservations.length > 0) {
        selectedTrackObservations = getState().timeline.selectedTrackObservations.map(
          item => item.Observation.observationId
        );
      }
      return { selectedItem, selectedTrackObservations };
    };

    const dispatchLayerFeatures = response => {
      const { selectedItem, selectedTrackObservations } = getSelectionState();
      dispatch(
        LayerActions.setLayerFeatures(
          projectId,
          layerId,
          response,
          selectedItem,
          selectedTrackObservations,
          // The reducer labels Agents features next to where it derives `age`,
          // so it needs the registration cache.
          getState().agentRegistry.byId
        )
      );

      // `signalKey` carries the layer name (see getAllLayerFeatures). Resolving
      // registrations here covers both the full refresh and the 20 s LKP tick.
      if (signalKey === 'Agents' && response && response.features) {
        dispatch(
          AgentRegistryActions.fetchMissingAgentRegistrations(response.features)
        );
      }
    };

    // Fetches every page, not just the first - see paginationUtil.js. The
    // requested pageLength stays the per-request chunk size; the total
    // number of results fetched is no longer capped at it.
    const fetchResponse = async () =>
      fetchAllFeaturePages(
        page =>
          map.features(
            signalKey,
            projectId,
            layerId,
            searchQuery,
            page.start,
            page.pageLength,
            requestParams
          ),
        { start, pageLength }
      );

    return fetchResponse().then(response => {
      if (!response.isError) {
        dispatchLayerFeatures(response);
      } else if (response.error.status === 401) {
        dispatch(localLogout());
      }
    });
  };
};

const getTracksForCurrentFilters = () => {
  return (dispatch, getState) => {
    const tracksLayer = getState().layer.layers.find(
      item => item.name === 'Tracks'
    );
    if (!tracksLayer) return Promise.resolve(null);

    const coordinates = {
      north: getState().query.north,
      east: getState().query.east,
      south: getState().query.south,
      west: getState().query.west
    };

    let query = null;
    if (tracksLayer.projectId == 'default') {
      query = createQuery(
        false,
        true,
        true,
        false,
        true,
        null,
        coordinates,
        getState().query.areas,
        getState().query.concepts,
        getState().query.mode,
        getState().query.dataSources,
        getState().query.agents
      );
    } else {
      query = createQuery(
        false,
        false,
        false,
        false,
        false,
        null,
        coordinates,
        getState().query.areas,
        getState().query.concepts,
        getState().query.mode,
        getState().query.dataSources,
        getState().query.agents
      );
    }

    if (tracksLayer.projectId == 'default') {
      if (getState().query.searchQuery !== '') {
        query.withQueryText(getState().query.searchQuery);
      }
      if (getState().query.start !== null && getState().query.end !== null) {
        query.withDateTimeRange(getState().query.end, getState().query.start);
      }
    }

    delete query.options;

    const requests = [];
    if (getState().layer.selectedLayers.includes(tracksLayer.name)) {
      requests.push(
        dispatch(
          getLayerFeatures(
            tracksLayer.name,
            tracksLayer.projectId,
            tracksLayer.id,
            query,
            1,
            TRACK_MARKER_PAGE_LENGTH
          )
        )
      );
    }
    requests.push(dispatch(LayerActions.getEntireTrack('track', '0', query)));

    return Promise.allSettled(requests);
  };
};

const setLastKnownUpdate = () => ({
  type: types.LAST_KNOWN_UPDATE,
  payload: {}
});

const clearCommonLayerFeatures = () => dispatch => {
  dispatch(LayerActions.clearCommonLayerFeatures());
};

export default {
  submitLogin,
  submitLogout,
  getAuthenticationStatus,
  localLogout,
  describeMap,
  getLayerFeatures,
  getAllLayerFeatures,
  getLayerHeatmap,
  getAlertLayerFeature,
  getAgentLayerFeatures,
  getSpecificLayer,
  getTracksForCurrentFilters,
  clearCommonLayerFeatures,
  setLastKnownUpdate
};
